import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { minMax } from '../lib/utils';
import { framePose } from '../lib/camera-framing';

// Viridis colormap (perceptually uniform)
const VIRIDIS_COLORS = [
  [0.267004, 0.004874, 0.329415],
  [0.282623, 0.140926, 0.457517],
  [0.253935, 0.265254, 0.529983],
  [0.206756, 0.371758, 0.553117],
  [0.163625, 0.471133, 0.558148],
  [0.127568, 0.566949, 0.550556],
  [0.134692, 0.658636, 0.517649],
  [0.266941, 0.748751, 0.440573],
  [0.477504, 0.821444, 0.318195],
  [0.741388, 0.873449, 0.149561],
  [0.993248, 0.906157, 0.143936]
];

function getViridisColor(t) {
  // t should be between 0 and 1
  const index = Math.min(Math.floor(t * (VIRIDIS_COLORS.length - 1)), VIRIDIS_COLORS.length - 2);
  const localT = (t * (VIRIDIS_COLORS.length - 1)) - index;
  
  const c1 = VIRIDIS_COLORS[index];
  const c2 = VIRIDIS_COLORS[index + 1];
  
  return [
    c1[0] + (c2[0] - c1[0]) * localT,
    c1[1] + (c2[1] - c1[1]) * localT,
    c1[2] + (c2[2] - c1[2]) * localT
  ];
}

// Orbit Controls Component
export function OrbitControls({ cameraCommand, sceneBounds, targetRef, apiRef }) {
  const { camera, gl } = useThree();
  const isDragging = useRef(false);
  const previousMousePosition = useRef({ x: 0, y: 0 });
  const spherical = useRef({ radius: 150, theta: 0, phi: Math.PI / 4 });
  // sceneBounds is a fresh object every render; read it through a ref so a camera
  // preset only fires on a new cameraCommand, not whenever the bounds object changes.
  const sceneBoundsRef = useRef(sceneBounds);
  sceneBoundsRef.current = sceneBounds;

  useEffect(() => {
    camera.position.set(0, 50, 150);
    camera.lookAt(0, 0, 0);

    const handleMouseDown = (e) => {
      isDragging.current = true;
      previousMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;

      const deltaX = e.clientX - previousMousePosition.current.x;
      const deltaY = e.clientY - previousMousePosition.current.y;

      // Shift / right-drag pans the orbit target across the ground (feeds the camera sync)
      const tgt = targetRef?.current;
      if (tgt && (e.shiftKey || (e.buttons & 2))) {
        const k = spherical.current.radius * 0.0015;
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0).setY(0).normalize();
        const fwd = new THREE.Vector3(tgt.x - camera.position.x, 0, tgt.z - camera.position.z).normalize();
        const move = right.multiplyScalar(-deltaX * k).add(fwd.multiplyScalar(deltaY * k));
        tgt.add(move);
        camera.position.add(move);
        previousMousePosition.current = { x: e.clientX, y: e.clientY };
        return;
      }

      spherical.current.theta -= deltaX * 0.01;
      spherical.current.phi -= deltaY * 0.01;
      spherical.current.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.current.phi));

      const x = spherical.current.radius * Math.sin(spherical.current.phi) * Math.sin(spherical.current.theta);
      const y = spherical.current.radius * Math.cos(spherical.current.phi);
      const z = spherical.current.radius * Math.sin(spherical.current.phi) * Math.cos(spherical.current.theta);

      const t = targetRef?.current;
      camera.position.set(x + (t ? t.x : 0), y + (t ? t.y : 0), z + (t ? t.z : 0));
      camera.lookAt(t ? t.x : 0, t ? t.y : 0, t ? t.z : 0);

      previousMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY * 0.1;
      spherical.current.radius += delta;
      spherical.current.radius = Math.max(10, Math.min(20000, spherical.current.radius));

      const x = spherical.current.radius * Math.sin(spherical.current.phi) * Math.sin(spherical.current.theta);
      const y = spherical.current.radius * Math.cos(spherical.current.phi);
      const z = spherical.current.radius * Math.sin(spherical.current.phi) * Math.cos(spherical.current.theta);

      const t = targetRef?.current;
      camera.position.set(x + (t ? t.x : 0), y + (t ? t.y : 0), z + (t ? t.z : 0));
      camera.lookAt(t ? t.x : 0, t ? t.y : 0, t ? t.z : 0);
    };

    gl.domElement.addEventListener('mousedown', handleMouseDown);
    gl.domElement.addEventListener('mousemove', handleMouseMove);
    gl.domElement.addEventListener('mouseup', handleMouseUp);
    const noMenu = (e) => e.preventDefault();
    gl.domElement.addEventListener('contextmenu', noMenu);
    gl.domElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      gl.domElement.removeEventListener('mousedown', handleMouseDown);
      gl.domElement.removeEventListener('mousemove', handleMouseMove);
      gl.domElement.removeEventListener('mouseup', handleMouseUp);
      gl.domElement.removeEventListener('contextmenu', noMenu);
      gl.domElement.removeEventListener('wheel', handleWheel);
    };
  }, [camera, gl, targetRef]);

  // Pose access for the saved view and the turntable: { position, target } read and written through the same orbit state
  useEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = {
      getPose: () => {
        const t = targetRef?.current;
        return { position: camera.position.toArray(), target: t ? t.toArray() : [0, 0, 0] };
      },
      setPose: ({ position, target }) => {
        if (targetRef?.current) targetRef.current.set(target[0], target[1], target[2]);
        camera.position.set(position[0], position[1], position[2]);
        camera.lookAt(target[0], target[1], target[2]);
        camera.updateProjectionMatrix();
        const dx = position[0] - target[0], dy = position[1] - target[1], dz = position[2] - target[2];
        const radius = Math.hypot(dx, dy, dz) || 1;
        spherical.current = { radius, phi: Math.acos(Math.min(1, Math.max(-1, dy / radius))) || 0.0001, theta: Math.atan2(dx, dz) };
      },
    };
    return () => { apiRef.current = null; };
  }, [apiRef, camera, targetRef]);

  // Camera presets (Top / Side / Perspective / Reset) driven from the View ribbon.
  // Re-derives the orbit's spherical state from the new position so dragging
  // continues smoothly afterward, instead of snapping back to the old orbit.
  useEffect(() => {
    if (!cameraCommand) return;
    const { id, lookAt: focusLookAt, lineLength, box, topTilt } = cameraCommand;

    // Frame the drawn content (bounding box of the models in the scene) when there is some
    const framed = framePose(box, id, { fov: camera.fov, aspect: camera.aspect, topTilt });
    if (framed) {
      const [px, py, pz] = framed.position, [tx, ty, tz] = framed.target;
      if (targetRef?.current) targetRef.current.set(tx, ty, tz);
      camera.position.set(px, py, pz);
      camera.lookAt(tx, ty, tz);
      camera.updateProjectionMatrix();
      const radius = Math.hypot(px - tx, py - ty, pz - tz) || 1;
      spherical.current = { radius, phi: Math.acos(Math.min(1, Math.max(-1, (py - ty) / radius))) || 0.0001, theta: Math.atan2(px - tx, pz - tz) };
      return;
    }

    if (id === 'reset') {
      if (focusLookAt) {
        const distance = Math.max(80, (lineLength || 100) * 0.8);
        camera.position.set(focusLookAt[0] + distance * 0.6, distance * 0.5, focusLookAt[2] + distance);
        camera.lookAt(focusLookAt[0], focusLookAt[1], focusLookAt[2]);
      } else {
        camera.position.set(0, 50, 150);
        camera.lookAt(0, 0, 0);
      }
      camera.updateProjectionMatrix();
      const center = focusLookAt || [0, 0, 0];
      if (targetRef?.current) targetRef.current.set(center[0], center[1], center[2]);
      const dx = camera.position.x - center[0], dy = camera.position.y - center[1], dz = camera.position.z - center[2];
      const radius = Math.hypot(dx, dy, dz) || 1;
      spherical.current = { radius, phi: Math.acos(Math.min(1, Math.max(-1, dy / radius))), theta: Math.atan2(dx, dz) };
      return;
    }

    const bounds = sceneBoundsRef.current;
    const [bx0, by0, bz0] = bounds?.min || [-50, -50, -50];
    const [bx1, by1, bz1] = bounds?.max || [50, 0, 50];
    const center = focusLookAt || [(bx0 + bx1) / 2, (by0 + by1) / 2, (bz0 + bz1) / 2];
    if (targetRef?.current) targetRef.current.set(center[0], center[1], center[2]);
    const diag = Math.hypot(bx1 - bx0, by1 - by0, bz1 - bz0) || 150;
    const R = Math.max(60, diag * 0.9);

    let pos;
    if (id === 'top') pos = [center[0], center[1] + R, center[2] + 0.001];
    else if (id === 'side') pos = [center[0] + R, center[1], center[2]];
    else pos = [center[0] + R * 0.55, center[1] + R * 0.45, center[2] + R * 0.75]; // perspective

    camera.position.set(pos[0], pos[1], pos[2]);
    camera.lookAt(center[0], center[1], center[2]);
    camera.updateProjectionMatrix();

    const dx = pos[0] - center[0], dy = pos[1] - center[1], dz = pos[2] - center[2];
    const radius = Math.hypot(dx, dy, dz) || 1;
    spherical.current = { radius, phi: Math.acos(Math.min(1, Math.max(-1, dy / radius))) || 0.0001, theta: Math.atan2(dx, dz) };
  }, [cameraCommand, camera, targetRef]);

  return null;
}

export default function PointCloudRenderer({ data, pointSize, colorMode, clippingPlanes }) {
  const pointsRef = useRef();
  const materialRef = useRef();
  const { positions, colors, intensity, classification, pointCount } = data;

  // Calculate colors based on mode - recompute when colorMode changes
  const displayColors = useMemo(() => {
    console.log('Recalculating colors for mode:', colorMode);
    
    if (colorMode === 'rgb') {
      return colors;
    } else if (colorMode === 'height') {
      const heightColors = new Float32Array(pointCount * 3);
      const zValues = [];
      
      for (let i = 0; i < pointCount; i++) {
        zValues.push(positions[i * 3 + 2]);
      }
      
      const { min: minZ, max: maxZ } = minMax(zValues);
      const range = maxZ - minZ || 1;

      for (let i = 0; i < pointCount; i++) {
        const z = positions[i * 3 + 2];
        const normalizedZ = (z - minZ) / range;
        
        const r = Math.min(1, Math.max(0, normalizedZ * 2));
        const g = Math.min(1, Math.max(0, 1 - Math.abs(normalizedZ - 0.5) * 2));
        const b = Math.min(1, Math.max(0, 1 - normalizedZ * 2));
        
        heightColors[i * 3] = r;
        heightColors[i * 3 + 1] = g;
        heightColors[i * 3 + 2] = b;
      }
      return heightColors;
    } else if (colorMode === 'viridis') {
      const viridisColors = new Float32Array(pointCount * 3);
      const zValues = [];
      
      for (let i = 0; i < pointCount; i++) {
        zValues.push(positions[i * 3 + 2]);
      }
      
      const { min: minZ, max: maxZ } = minMax(zValues);
      const range = maxZ - minZ || 1;

      for (let i = 0; i < pointCount; i++) {
        const z = positions[i * 3 + 2];
        const t = (z - minZ) / range;
        const color = getViridisColor(t);
        
        viridisColors[i * 3] = color[0];
        viridisColors[i * 3 + 1] = color[1];
        viridisColors[i * 3 + 2] = color[2];
      }
      return viridisColors;
    } else if (colorMode === 'intensity' && intensity && intensity.length > 0) {
      const intensityColors = new Float32Array(pointCount * 3);
      const { max: maxIntensity } = minMax(intensity);
      
      for (let i = 0; i < pointCount; i++) {
        const norm = intensity[i] / (maxIntensity || 1);
        intensityColors[i * 3] = norm;
        intensityColors[i * 3 + 1] = norm;
        intensityColors[i * 3 + 2] = norm;
      }
      return intensityColors;
    } else if (colorMode === 'classification' && classification && classification.length > 0) {
      const classColors = {
        0: [0.5, 0.5, 0.5],
        1: [0.6, 0.4, 0.2],
        2: [0.4, 0.8, 0.3],
        3: [0.2, 0.7, 0.2],
        4: [0.1, 0.5, 0.1],
        5: [0.9, 0.3, 0.3],
        6: [0.2, 0.4, 0.9],
      };
      
      const classificationColors = new Float32Array(pointCount * 3);
      for (let i = 0; i < pointCount; i++) {
        const classId = classification[i];
        const color = classColors[classId] || [0.5, 0.5, 0.5];
        
        classificationColors[i * 3] = color[0];
        classificationColors[i * 3 + 1] = color[1];
        classificationColors[i * 3 + 2] = color[2];
      }
      return classificationColors;
    }
    
    return colors;
  }, [colorMode, colors, positions, intensity, classification, pointCount]);

  // Gentle auto-rotation (the beautiful surprise!)
  useFrame(() => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += 0.002;
    }
  });

  // Update geometry when colors change
  useEffect(() => {
    if (pointsRef.current && displayColors) {
      pointsRef.current.geometry.attributes.color.array = displayColors;
      pointsRef.current.geometry.attributes.color.needsUpdate = true;
    }
  }, [displayColors]);

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={0.5} />
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={pointCount}
            array={positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={pointCount}
            array={displayColors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          ref={materialRef}
          size={pointSize}
          vertexColors
          sizeAttenuation={true}
          transparent={true}
          opacity={1.0}
          alphaTest={0.5}
          clippingPlanes={clippingPlanes}
          map={createCircleTexture()}
        />
      </points>
    </>
  );
}

// Create circular point texture
function createCircleTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.8)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
